import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingActionWidget from "./components/FloatingActionWidget";
import { trackWhatsAppClick } from "./utils/trackWhatsApp";

// Lazy load pages for better performance
const Home = lazy(() => import("./pages/Home"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PackageDetailPage = lazy(() => import("./components/PackageDetailPage"));
const LeadCapturePopup = lazy(() => import("./components/LeadCapturePopup"));
const BlogPost = lazy(() => import("./components/BlogPost"));
const ProposalPage = lazy(() => import("./pages/ProposalPage"));
const QuotePage = lazy(() => import("./pages/QuotePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const StaffDashboard = lazy(() => import("./pages/StaffDashboard"));
const PackageProposalPage = lazy(() => import("./pages/PackageProposalPage"));

// Layout wrapper to conditionally show Navbar/Footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminPage = location.pathname === '/admin' || location.pathname === '/staff';
  const isProposalPage = location.pathname.startsWith('/proposal/') || location.pathname.startsWith('/p/');
  const hideChrome = isAdminPage || isProposalPage;

  // Global WhatsApp click tracker — delegated listener catches every wa.me click
  useEffect(() => {
    const handler = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href*="wa.me"]');
      if (!anchor) return;
      // Infer button location from data attribute or nearest semantic ancestor
      const location = anchor.dataset.waLocation
        || anchor.closest('header') && 'header'
        || anchor.closest('footer') && 'footer'
        || anchor.closest('[data-testid="floating-widget"]') && 'floating'
        || 'body';
      const packageName = anchor.dataset.waPackage || null;
      trackWhatsAppClick({ location, packageName });
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);
  
  return (
    <>
      {!hideChrome && <Navbar />}
      <Suspense fallback={<div className="min-h-screen" aria-hidden="true" />}>
        {children}
      </Suspense>
      {!hideChrome && <Footer />}
      {!hideChrome && <FloatingActionWidget />}
      {!hideChrome && (
        <Suspense fallback={null}>
          <LeadCapturePopup />
        </Suspense>
      )}
    </>
  );
};

function App() {
  // Reveal content as soon as React mounts (don't wait for window 'load' / all images) — improves mobile LCP & Speed Index
  useEffect(() => {
    const loader = document.getElementById('initial-loader');
    if (loader) loader.classList.add('hide');
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/proposal/:slug" element={<ProposalPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/quote" element={<QuotePage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/staff" element={<StaffDashboard />} />
              <Route path="/p/:cid" element={<PackageProposalPage />} />
              
              {/* Dynamic Package Detail Page */}
              <Route path="/packages/:slug" element={<PackageDetailPage />} />
              <Route path="/packages" element={<Navigate to="/#packages" replace />} />

              {/* Catch-all 404 — never render a blank page */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
